import {
  BankOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  ShopOutlined,
  TagsOutlined
} from '@ant-design/icons';
import { Button, Divider, Dropdown, MenuProps, Typography } from 'antd';
import {
  COMPLEMENTARY_ACTIVITY_ORIGIN_MAP,
  COMPLEMENTARY_ACTIVITY_STATUS_MAP,
  getComplementaryActivityFileUrl
} from 'services/graduate-enrollment/complementary-activities';
import { ComplementaryActivityFileViewer } from './ComplementaryActivityFileViewer';
import {
  ComplementaryActivityStatus,
  IComplementaryActivity
} from 'interfaces/complementary_activities';
import { ReactNode, useState } from 'react';
import { TagField } from '@refinedev/antd';
import { downloadFileFromBlob } from 'utils/donwload-from-blob';
import { formatDate } from 'services/date';
import { tagColorByStatus } from 'utils/statusColorMapping';
import { useNotification } from '@refinedev/core';

/**
 * O bloco de horas diz o que a AAC faz no total, como na área do aluno:
 * aprovada conta, pendente aguarda a análise e rejeitada não conta (riscada).
 */
const HOURS_TILE: Record<
  ComplementaryActivityStatus,
  { className: string; label: string }
> = {
  approved: {
    className: 'bg-green-50 text-green-700',
    label: 'horas que contam no total'
  },
  pending: {
    className: 'bg-gray-100 text-gray-700',
    label: 'horas aguardando análise'
  },
  rejected: {
    className: 'bg-gray-100 text-gray-400 line-through',
    label: 'horas que não contam no total'
  }
};

// Um ícone por origem, como nas opções do formulário.
const ORIGIN_ICONS = {
  internal: <BankOutlined />,
  external: <ShopOutlined />
};

const HoursTile = ({ activity }: { activity: IComplementaryActivity }) => {
  const tile = HOURS_TILE[activity.status];

  return (
    <div
      aria-label={`${activity.hours} ${tile.label}`}
      title={`${activity.hours}h: ${tile.label}`}
      className={`flex h-10 min-w-[40px] shrink-0 items-center justify-center gap-1 rounded-lg px-2 text-base font-semibold tabular-nums leading-none ${tile.className}`}
    >
      <ClockCircleOutlined aria-hidden className="text-xs opacity-70" />
      <span>
        {activity.hours}
        <span className="text-xs font-medium">h</span>
      </span>
    </div>
  );
};

const MetaItem = ({
  icon,
  children
}: {
  icon: ReactNode;
  children: ReactNode;
}) => (
  <span className="flex min-w-0 items-center gap-1.5 text-gray-500">
    <span className="shrink-0 text-gray-400">{icon}</span>
    <span className="truncate">{children}</span>
  </span>
);

const Detail = ({
  label,
  children
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex min-w-0 flex-col gap-0.5">
    <Typography.Text type="secondary" className="text-xs">
      {label}
    </Typography.Text>
    <div className="min-w-0">{children}</div>
  </div>
);

const formatDay = (date: string) => formatDate(date, 'DD/MM/YYYY');

const period = ({ starts_on, ends_on }: IComplementaryActivity) =>
  starts_on === ends_on
    ? formatDay(starts_on)
    : `${formatDay(starts_on)} a ${formatDay(ends_on)}`;

interface ComplementaryActivityCardProps {
  enrollmentId: string;
  activity: IComplementaryActivity;
  allowEdit: boolean;
  allowApprove: boolean;
  allowReject: boolean;
  allowDelete: boolean;
  onEdit: (activity: IComplementaryActivity) => void;
  onApprove: (activity: IComplementaryActivity) => void;
  onReject: (activity: IComplementaryActivity) => void;
  onDelete: (activity: IComplementaryActivity) => void;
}

export const ComplementaryActivityCard = ({
  enrollmentId,
  activity,
  allowEdit,
  allowApprove,
  allowReject,
  allowDelete,
  onEdit,
  onApprove,
  onReject,
  onDelete
}: ComplementaryActivityCardProps) => {
  const [viewingFile, setViewingFile] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { open } = useNotification();
  const isPending = activity.status === 'pending';
  // Pendente é a fila da secretaria: aprovar e rejeitar ficam à vista no card.
  // Rever uma decisão já tomada continua no menu.
  const showReviewButtons = isPending && (allowApprove || allowReject);
  // Aprovada segue editável, inclusive as horas. A API exige approve além de edit.
  const canEdit =
    activity.status === 'approved' ? allowEdit && allowApprove : allowEdit;

  const menuItems: MenuProps['items'] = [
    allowApprove &&
      activity.status === 'rejected' && {
        key: 'approve',
        label: 'Aprovar',
        icon: <CheckCircleOutlined />,
        onClick: () => onApprove(activity)
      },
    allowReject &&
      activity.status === 'approved' && {
        key: 'reject',
        label: 'Rejeitar',
        icon: <CloseCircleOutlined />,
        onClick: () => onReject(activity)
      },
    canEdit && {
      key: 'edit',
      label: 'Editar',
      icon: <EditOutlined />,
      onClick: () => onEdit(activity)
    },
    // Aprovada já conta nas horas: precisa ser rejeitada antes de sair.
    allowDelete &&
      activity.status !== 'approved' && {
        key: 'delete',
        label: 'Excluir',
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => onDelete(activity)
      }
  ].filter(Boolean) as MenuProps['items'];

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const url = await getComplementaryActivityFileUrl(
        enrollmentId,
        activity.id
      );
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('download failed');
      }

      downloadFileFromBlob(await response.blob(), activity.file_name);
    } catch {
      open?.({
        type: 'error',
        message: 'Ops!',
        description: 'Erro ao baixar o comprovante. Tente novamente.'
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-solid border-gray-200 bg-white">
      <div className="flex items-center gap-3 bg-gray-50 px-4 py-2">
        <HoursTile activity={activity} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Typography.Text strong className="truncate text-base">
            {activity.event_name}
          </Typography.Text>
          <div className="flex min-w-0 flex-col gap-1 text-sm md:flex-row md:gap-4">
            <MetaItem icon={ORIGIN_ICONS[activity.origin]}>
              {COMPLEMENTARY_ACTIVITY_ORIGIN_MAP[activity.origin]}
            </MetaItem>
            <MetaItem icon={<TagsOutlined />}>
              {activity.activity_type}
            </MetaItem>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <TagField
            className="!mr-0 !inline-flex !h-8 !items-center !px-2.5 !text-sm !leading-none"
            color={tagColorByStatus(activity.status)}
            value={COMPLEMENTARY_ACTIVITY_STATUS_MAP[activity.status].text}
          />
          {menuItems && menuItems.length > 0 && (
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button icon={<EllipsisOutlined />} aria-label="Ações da AAC" />
            </Dropdown>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4 border-x-0 border-b-0 border-t border-solid border-gray-100 p-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-stretch md:gap-6">
          <div className="min-w-0 md:flex-1">
            <Detail label="Local">
              <Typography.Text>{activity.location}</Typography.Text>
            </Detail>
          </div>
          <Divider
            type="vertical"
            className="!relative !top-0 !mx-0 !hidden !h-auto !self-stretch !border-gray-200 md:!inline-block"
          />
          <div className="min-w-0 md:flex-1">
            <Detail label="Período">
              <Typography.Text>{period(activity)}</Typography.Text>
            </Detail>
          </div>
          <Divider
            type="vertical"
            className="!relative !top-0 !mx-0 !hidden !h-auto !self-stretch !border-gray-200 md:!inline-block"
          />
          <div className="min-w-0 md:flex-[2]">
            <Detail label="Comprovante">
              <div className="flex min-w-0 items-center gap-2">
                <Typography.Text className="min-w-0 flex-1 truncate">
                  {activity.file_name}
                </Typography.Text>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  aria-label="Visualizar"
                  onClick={() => setViewingFile(true)}
                />
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  aria-label="Baixar"
                  loading={downloading}
                  onClick={handleDownload}
                />
              </div>
            </Detail>
          </div>
        </div>

        {activity.status === 'rejected' && activity.feedback && (
          <>
            <Divider className="!my-0 !border-gray-200" />
            <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2">
              <ExclamationCircleOutlined className="mt-1 text-red-600" />
              <Typography.Text>
                <Typography.Text strong className="text-red-700">
                  Motivo da rejeição:
                </Typography.Text>{' '}
                {activity.feedback}
              </Typography.Text>
            </div>
          </>
        )}

        {showReviewButtons && (
          <>
            <Divider className="!my-0 !border-gray-200" />
            <div className="flex flex-wrap items-center justify-end gap-2">
              {allowReject && (
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={() => onReject(activity)}
                >
                  Rejeitar
                </Button>
              )}
              {allowApprove && (
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={() => onApprove(activity)}
                >
                  Aprovar
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <ComplementaryActivityFileViewer
        enrollmentId={enrollmentId}
        activity={activity}
        open={viewingFile}
        onClose={() => setViewingFile(false)}
      />
    </div>
  );
};
